import { useEffect, useState } from 'react';
import { GRID_COLS, GRID_ROWS } from '@/pages/spatial-span-spick/gameConfig';

export interface ViewportMetrics {
  height: number;
  tileSize: number;
  boardPadding: number;
  guidanceHeight: number;
  sequenceHeight: number;
  screenPt: number;
  screenPb: number;
  sequenceMt: number;
}

// 스픽 버전 상단 네비게이션 바 높이 (뒤로가기/홈)
const TOP_BAR_HEIGHT = 56;
// 게임 단계 박스 높이
const STAGE_HEIGHT = 44;
// 게임판 하단 최소 여백 (12~16px 확보)
const BOARD_BOTTOM_GAP = 16;
// 게임 화면 좌우 패딩 (px-5)
const HORIZONTAL_PADDING = 40;
// 컨텐츠 최대 폭
const MAX_APP_WIDTH = 430;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// 실제 사용 가능한 viewport 높이를 읽어 레이아웃 지표를 계산한다.
// 브라우저/WebView 주소창·하단 메뉴 등 UI 변화는 visualViewport.height 로 감지하고,
// 100dvh 를 fallback 으로 사용해 주소창이 접히고 펼쳐져도 레이아웃이 깨지지 않게 한다.
function computeMetrics(): ViewportMetrics {
  const visualViewport =
    typeof window !== 'undefined' ? window.visualViewport : undefined;
  const height = Math.round(
    visualViewport?.height ??
      (typeof window !== 'undefined' ? window.innerHeight : 800),
  );
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 390;
  const appWidth = Math.min(viewportWidth, MAX_APP_WIDTH);

  // 화면 높이에 따라 상하 여백 / 고정 영역을 우선 축소
  let screenPt: number;
  let guidanceHeight: number;
  let sequenceHeight: number;
  let sequenceMt: number;
  let boardPadding: number;

  if (height >= 780) {
    screenPt = 16;
    guidanceHeight = 76;
    sequenceHeight = 40;
    sequenceMt = 8;
    boardPadding = 20;
  } else if (height >= 660) {
    screenPt = 12;
    guidanceHeight = 68;
    sequenceHeight = 36;
    sequenceMt = 6;
    boardPadding = 14;
  } else if (height >= 560) {
    screenPt = 8;
    guidanceHeight = 58;
    sequenceHeight = 32;
    sequenceMt = 4;
    boardPadding = 8;
  } else {
    screenPt = 6;
    guidanceHeight = 52;
    sequenceHeight = 30;
    sequenceMt = 4;
    boardPadding = 6;
  }

  // 게임판 아래 여백은 어떤 화면에서도 최소 16px 을 확보한다
  const screenPb = height >= 780 ? 20 : BOARD_BOTTOM_GAP;

  // 게임판이 실제로 사용할 수 있는 높이 (상단바 + 단계 + 안내 + 하단여백 제외)
  const boardHeight = Math.max(
    height - TOP_BAR_HEIGHT - STAGE_HEIGHT - guidanceHeight - screenPb,
    200,
  );
  const boardWidth = Math.max(appWidth - HORIZONTAL_PADDING, 200);

  // 셀 안에 정확히 들어가는 정사각 타일 크기 (가로/세로 중 작은 쪽 기준)
  const cellHeight = (boardHeight - boardPadding * 2) / GRID_ROWS;
  const cellWidth = (boardWidth - boardPadding * 2) / GRID_COLS;
  const tileSize = clamp(
    Math.floor(Math.min(cellWidth, cellHeight, 88)),
    34,
    88,
  );

  return {
    height,
    tileSize,
    boardPadding,
    guidanceHeight,
    sequenceHeight,
    screenPt,
    screenPb,
    sequenceMt,
  };
}

// CSS 변수를 문서 루트에 주입해 하위 컴포넌트가 var(--*)로 소비할 수 있게 한다.
function applyMetrics(metrics: ViewportMetrics): void {
  const root = document.documentElement.style;
  root.setProperty('--sh-app-height', `${metrics.height}px`);
  root.setProperty('--sh-tile-size', `${metrics.tileSize}px`);
  root.setProperty('--sh-board-padding', `${metrics.boardPadding}px`);
  root.setProperty('--sh-guidance-height', `${metrics.guidanceHeight}px`);
  root.setProperty('--sh-sequence-height', `${metrics.sequenceHeight}px`);
  root.setProperty('--sh-screen-pt', `${metrics.screenPt}px`);
  root.setProperty('--sh-screen-pb', `${metrics.screenPb}px`);
  root.setProperty('--sh-sequence-mt', `${metrics.sequenceMt}px`);
}

export function useViewportMetrics(): ViewportMetrics {
  const [metrics, setMetrics] = useState<ViewportMetrics>(() => computeMetrics());

  useEffect(() => {
    const visualViewport = window.visualViewport;
    const update = () => {
      const next = computeMetrics();
      setMetrics(next);
      applyMetrics(next);
    };

    update();

    visualViewport?.addEventListener('resize', update);
    visualViewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    return () => {
      visualViewport?.removeEventListener('resize', update);
      visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return metrics;
}